/**
 * useCachedFetch
 * ──────────────
 * Stale-while-revalidate hook backed by `localStorage` (survives full
 * app close/reopen — unlike sessionStorage).
 *
 * Behaviour (matches the user-chosen "Cached instant reveal" strategy):
 *   1. On mount, read cached data SYNCHRONOUSLY inside the useState
 *      initializer. If the cache hit exists, the very first render
 *      already has real data — no loading state, no pop-in.
 *   2. Fire a background fetch. When it resolves, update state AND
 *      overwrite the cache atomically. If nothing changed the setState
 *      is a no-op thanks to a shallow-JSON compare.
 *   3. On cold launch (no cache), `data` is `null` and `isValidating`
 *      is `true` — callers render a skeleton until the first fetch
 *      lands. Returning-user launches skip this branch entirely.
 *
 * Cache format:  { v: <version>, t: <epoch-ms>, d: <payload> }
 * Version bump automatically invalidates all stale cache entries so
 * we never render data from an incompatible schema.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// Bump when payload shapes change app-wide. Individual callers can
// also pass their own `version` for surgical invalidation.
const CACHE_VERSION = 1;
const KEY_PREFIX = 'eili_cache_v1:';

// Simple shallow-equal on JSON strings — good enough to skip renders
// when the fresh response equals the cached one.
function equalJSON(a, b) {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

function readCache(key, version) {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== version) return null;
    return parsed;
  } catch { return null; }
}

function writeCache(key, version, data) {
  try {
    localStorage.setItem(KEY_PREFIX + key, JSON.stringify({
      v: version, t: Date.now(), d: data,
    }));
  } catch { /* quota exceeded / private mode — silent */ }
}

/**
 * @param {string|null} key   Cache key. Pass `null` to skip fetch+cache
 *                            entirely (e.g. waiting on a param). Must
 *                            be stable across renders — derive with
 *                            useMemo if it depends on inputs.
 * @param {() => Promise}  fetcher    Async function returning the payload.
 * @param {object} [opts]
 * @param {number} [opts.version]   Per-caller schema version (default: 1)
 * @param {any}    [opts.fallback]  Initial data when no cache exists
 * @param {boolean}[opts.enabled]   Set false to defer the fetch
 */
export default function useCachedFetch(key, fetcher, opts = {}) {
  const version = opts.version ?? CACHE_VERSION;
  const enabled = opts.enabled !== false;

  // Synchronous cache read inside useState initializer — the first
  // render already has the cached data if it exists. This is what
  // eliminates pop-in on remount.
  const [state, setState] = useState(() => {
    if (!key) return { data: opts.fallback ?? null, hasCached: false, cachedAt: null };
    const cached = readCache(key, version);
    if (cached) return { data: cached.d, hasCached: true, cachedAt: cached.t };
    return { data: opts.fallback ?? null, hasCached: false, cachedAt: null };
  });
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState(null);

  // Latest-fetcher ref so re-renders don't retrigger the effect.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const revalidate = useCallback(async () => {
    if (!key || !enabled) return;
    setIsValidating(true);
    setError(null);
    try {
      const fresh = await fetcherRef.current();
      setState((prev) => {
        // Skip re-render if payload is byte-identical to cached data.
        if (prev.hasCached && equalJSON(prev.data, fresh)) {
          writeCache(key, version, fresh); // still refresh the timestamp
          return prev;
        }
        writeCache(key, version, fresh);
        return { data: fresh, hasCached: true, cachedAt: Date.now() };
      });
    } catch (err) {
      // Preserve cached data on error — a stale render beats an
      // empty screen. Callers can check `error` to show a subtle
      // "couldn't refresh" toast if they want.
      setError(err);
    } finally {
      setIsValidating(false);
    }
  }, [key, version, enabled]);

  useEffect(() => {
    if (!key || !enabled) return;
    revalidate();
    // Intentionally only re-fires when key/enabled changes. Fetcher
    // identity is read from a ref so callers can inline it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  // Manual setter — lets callers optimistically update the cache
  // after mutations (e.g. mark day complete, dismiss item) without
  // waiting for the next background revalidate to catch up.
  const mutate = useCallback((updater) => {
    setState((prev) => {
      const next = typeof updater === 'function' ? updater(prev.data) : updater;
      if (key) writeCache(key, version, next);
      return { data: next, hasCached: true, cachedAt: Date.now() };
    });
  }, [key, version]);

  return {
    data: state.data,
    hasCached: state.hasCached,
    cachedAt: state.cachedAt,
    isValidating,
    // `isColdLoading` is TRUE only on the first-ever visit while the
    // fetch is in flight. Callers render skeletons off this flag —
    // never off `isValidating` (which would flash on every revisit).
    isColdLoading: isValidating && !state.hasCached,
    error,
    refetch: revalidate,
    mutate,
  };
}
